import { z } from 'zod';

/**
 * Brand form validation.
 *
 * Mirrors the backend's own constraints so the user hears about a problem
 * before a round trip rather than after one:
 *   - Name and Arabic name are [Required], [StringLength(50)] on the Admin API
 *   - CategoryId must reference a real category (CATEGORY_NOT_FOUND if not)
 *
 * ---------------------------------------------------------------------------
 * WHY THERE ARE NO `.transform()` OR `z.coerce` CALLS HERE
 *
 * Both make a schema's input type differ from its output type. React Hook Form
 * infers its field types from the input side and its submit handler from the
 * output side, so a transforming schema produces a `Resolver<A> is not
 * assignable to Resolver<B>` error that is tempting to silence with `any`.
 *
 * Instead the schema is input === output: optional text fields are plain
 * strings that may be empty, and numbers are real numbers converted at the
 * input's `onChange`. Turning '' into null happens once, at submit, in
 * `brand-form-dialog.tsx`. Validation stays honest and the types stay clean.
 *
 * Frontend validation is a convenience, never the guarantee — the backend
 * validates independently and its errors are mapped back onto these same
 * fields through `ApiError.fieldErrors`.
 * ------------------------------------------------------------------------- */

/** Optional text: empty is allowed, and normalised to null at submit. */
const optionalText = (max: number, message?: string) =>
  z
    .string()
    .trim()
    .max(max, message ?? `Must be ${max} characters or fewer.`);

export const brandSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Brand name is required.')
    .max(50, 'Brand name must be 50 characters or fewer.'),

  arName: z
    .string()
    .trim()
    .min(1, 'Arabic name is required.')
    .max(50, 'Arabic name must be 50 characters or fewer.'),

  categoryId: z.number({ message: 'Select a category.' }).int().positive('Select a category.'),

  slug: optionalText(100).refine(
    (value) => value === '' || /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value),
    'Use lowercase letters, numbers and hyphens only (e.g. "google-play").',
  ),

  description: optionalText(500),

  imageUrl: optionalText(500).refine(
    (value) => value === '' || z.url().safeParse(value).success,
    'Enter a valid URL.',
  ),

  /** Used on create only; an existing brand's status changes through its own confirmed action. */
  isActive: z.boolean(),
});

export type BrandFormValues = z.infer<typeof brandSchema>;

/** Empty strings are stored as NULL, not '', so the column stays meaningful. */
export function emptyToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}
